import {RowData} from '../components/Sheet/types';
import {UserRole} from '../security/auth';
import {ReactNode} from 'react';
import {FormSpec} from '../components/Admin/LeadForm/leadFormTypes.ts';
import {themes} from '../components/Ui/themes/themes.ts';
import {Highlights} from '../components/Admin/Search/Highlight.tsx';
import {SVGSpec} from '../components/Admin/Form/Picture/svgTypes.ts';
import {JSONContent} from '@tiptap/core';
import {AclObjectType} from '../components/Admin/Acl/acl.ts';

export type ViolationList = {
    violations?: Violation[];
};

export type Violation = {
    propertyPath: string;
    title: string;
};

export type WithIRI<T extends object> = {
    '@id'?: string;
} & T;

export type MemberPermission = {
    id: string | null;
    objectType: string;
    objectId: string | null;
    objectName: string | null;
    mask: number;
    parentId: string | null;
    /** Set on an entry a member holds through one of their teams. */
    teamId?: string | null;
    teamName?: string | null;
    resolving?: boolean;
};

/**
 * Why an account cannot be deleted yet: each is about the organization the
 * user owns, and something they can sort out themselves.
 */
export enum AccountDeletionBlocker {
    ManagedSubscription = 'managed_subscription',
}

/** What deleting their account would do, shown before they confirm. */
export type AccountDeletionPreview = {
    blocker?: AccountDeletionBlocker;
    /** They own their organization: it goes with them, members included. */
    organizationDeleted: boolean;
    organizationName?: string;
    membersDeletedAlong: number;
    /** Its Chargebee subscription stops renewing as soon as they confirm. */
    subscriptionCancelled: boolean;
};

export enum OrganizationRole {
    Admin = 'ORG_ROLE_ADMIN',
    /** Can create profiles anywhere in the organization. */
    ProfileCreator = 'ORG_ROLE_PROFILE_CREATOR',
    /** Can list the members when they cannot all see each other. */
    MemberViewer = 'ORG_ROLE_MEMBER_VIEWER',
    /** Handles every lead of the organization, as administrators do when it allows them to. */
    LeadManager = 'ORG_ROLE_LEAD_MANAGER',
}

export type User = WithIRI<{
    id: string;
    adminFullTitle: string;
    displayName: string;
    fullName?: string;
    name?: string;
    email: string;
    emailConfirmed: boolean;
    locale?: Readonly<string>;
    team: boolean;
    invite?: MemberInvite;
    account: UserAccount;
    privileged?: boolean;
    disabled?: boolean;
    organizationAdmin?: boolean;
    organizationOwner?: boolean;
    organizationRoles?: OrganizationRole[];
    organization: Organization;
    highlights?: {
        displayName?: string[];
        organizationName?: string[];
        accountEmail?: string[];
    };
    createdAt: string;
}>;

export type UserAccount = {
    id: string;
    email: string;
    emailConfirmed: boolean;
    passwordCreatedAt?: string;
};

export type MfaSetup = {
    mfaRequired: boolean;
    email: boolean;
    totp: boolean;
};

export type MfaTotpSetup = {
    totpSecret: string;
    totpSecretQR: string;
    setupId: string;
} & MfaSetup;

export type Jwt = {
    exp: number;
    em: string;
    uid: string;
    ec: boolean;
    rol?: UserRole[];
    imp?: string;
    mfa?: string;
};

export type Image = {
    id: string;
    url: string;
};

export type Logo = {
    id: string;
    thumbUrl: string;
};

export type Cover = {
    id: string;
    url: string;
    options: CoverOptions;
};

export type SocialLink = {
    type: string;
    value: string;
    trackingUrl?: string;
    notInSignature?: boolean;
};

export type Email = {
    value: string;
};

export type Phone = {
    value: string;
    vcfTypes: string[];
};

export type IconType = 'apple' | 'playStore' | 'calendar' | 'link';

export type Link = {
    id: string;
    label: string;
    url: string;
    icon?: IconType | '' | undefined;
    translations?: Translations;
};

export type Address = {
    address: string;
    address2?: string | undefined;
    region?: string | undefined;
    city: string;
    zipCode: string;
    country: string;
};

export type WithLinks = {
    links: WithIRI<Link>[];
};

export type WithPhones = {
    phones: WithIRI<Phone>[];
};

export type WithEmails = {
    emails: WithIRI<Email>[];
};

export enum RichBlockType {
    Video = 'video',
    Image = 'image',
    Images = 'images',
    Text = 'text',
    Document = 'document',
}

export type RichBlockData = {
    id: string;
    [key: string]: any;
    translations?: Translations;
};

export type TextBlockData = {
    title?: string;
    text?: string;
} & RichBlockData;

export type VideoBlockData = {
    title?: string;
    url?: string;
} & RichBlockData;

export type DocumentBlockData = {
    title?: string;
    asset?: Asset;
    trackingUrl?: string;
} & RichBlockData;

export type ImageBlockData = {
    url?: string;
} & DocumentBlockData;

export type RichBlock = {
    id: string;
    type: RichBlockType;
    data: RichBlockData;
};

export type WithRichBlocks = {
    blocks?: WithIRI<RichBlock>[];
};

export type WithSocials = {
    socials: WithIRI<SocialLink>[];
};

export type WithAddresses = {
    addresses: WithIRI<Address>[];
};

type CoverOptions = {
    scale?: number | undefined;
    bgColor?: string | undefined;
};

export type ThemeName = keyof typeof themes;

export type ThemeConfig = {
    name: ThemeName;
    custom?: Record<string, any>;
};

export type Person = {
    id: string;
    organization?: Organization;
    organizationId: string;
    translations?: Translations;
    tags: Tag[];
    locale: string | undefined;
    country?: string | undefined;
    cover?: Cover | null;
    coverOptions?: CoverOptions;
    appClipHeader?: Image | null;
    background?: Image | null;
    openGraphImage?: Image | null;
    note?: string | null | undefined;
    bio?: string | null | undefined;
    keywords?: string[] | null | undefined;
    openGraphTitle?: string | undefined;
    openGraphDescription?: string | undefined;
    leadEmailReplyTo?: string | null;
    leadWelcomeEmailSubject?: string | null;
    leadWelcomeEmailBody?: string | null;
} & WithAddresses &
    WithPhones &
    WithEmails &
    WithSocials &
    WithRichBlocks &
    WithLinks;

export type BatchMedium = {
    id: string;
    name: string;
};

export type Tag = {
    id: string;
    name: string;
    color?: string;
};

export type Kompany = WithIRI<{
    displayName: string;
    internalName?: string;
    logo?: Logo | null;
    siteUrl?: string | undefined;
    trackingSiteUrl?: string | undefined;
    theme?: ThemeConfig | null;
    permissions: {
        edit: boolean;
        delete: boolean;
        acl: boolean;
        createProfile: boolean;
    };
    fieldPermissions?: FieldPermissionsRules;
    highlights?: {
        internalName?: string[];
        displayName?: string[];
        phones?: string[];
        emails?: string[];
        siteUrl?: string[];
    };
    profileCount?: number;
    owner: User;
}> &
    Person;

export type FieldTranslations = Record<string, string>;
export type Translations = Record<string, FieldTranslations>;

export type FieldPermissionsRules = {
    write?: string[];
};

export type Profile = {
    kompany?: WithIRI<Kompany> | null;
    internalName?: string | null;
    title?: string | null;
    department?: string | null;
    avatar?: Image | null;
    fullName?: string;
    firstName?: string;
    appClipTitle?: string | undefined;
    appClipSubtitle?: string | undefined;
    defaultAppClipTitle?: Readonly<string>;
    defaultAppClipSubtitle?: Readonly<string>;
    lastName?: string | null | undefined;
    safeKey?: string | undefined;
    vcfUrl: string | undefined;
    manifestUrl: string;
    ogImageUrl?: string;
    ogTitle?: string;
    ogDescription?: string;
    theme?: ThemeConfig | undefined;
    resolvedTheme?: ThemeConfig;
    suggestedHolder?: User | undefined;
    profileLabel: string;
    profileShortLabel: string;
    enabled: boolean;
    resolvedEnabled: boolean;
    shareUrl: string;
    customShareUrl?: string | undefined;
    vcfShareUrl?: string | undefined;
    options: {
        exchanges?: boolean;
        autoOpenForm?: boolean;
        paperScan?: boolean;
        noBranding?: boolean;
    };
    permissions: {
        edit: boolean;
        changeKompany: boolean;
        delete: boolean;
        duplicate: boolean;
        acl: boolean;
        changeHolder: boolean;
    };
    fieldPermissions?: FieldPermissionsRules;
    leadForm?: LeadForm;
    expired?: boolean;
    locked?: boolean;
    hideCompanyName?: boolean;
    exchangesEnabled?: boolean;
    leadWelcomeEmailEnabled?: boolean;
    autoOpenForm?: boolean;
    noProfileLink?: boolean;
    appClipEnabled?: boolean;
    mediums?: {
        id: string;
        permissions: MediumPermissions;
    }[];
    highlights?: {
        internalName?: string[];
        firstName?: string[];
        lastName?: string[];
        kompanyName?: string[];
        title?: string[];
    };
    owner: User;
    holder?: User | undefined;
    follow?: boolean;
} & Person;

type MediumPermissions = {
    edit: boolean;
    delete: boolean;
    switchProfile: boolean;
    acl: boolean;
    changeHolder: boolean;
    disable: boolean;
    enable: boolean;
};

export enum MediumType {
    Card = 'card',
    Stars = 'stars',
    Extra = 'extra',
}

export enum MediumProductType {
    Card = 'card',
    Deck = 'deck',
    Square = 'square',
    Keychain = 'keychain',
    Nano = 'nano',
    Dome = 'dome',
}

export type Medium = {
    id: string;
    owner: User | null;
    holder?: User | undefined;
    organization?: Organization;
    organizationId: string | null;
    name: string;
    profile: WithIRI<Profile> | null;
    safeKey: string;
    activateKey?: string | undefined;
    assigned: boolean;
    enabled: boolean;
    type: MediumType;
    productType: MediumProductType;
    expired: boolean;
    tags: Tag[];
    locked: boolean;
    activated: boolean;
    productCode: string;
    productTitle: string;
    openVCF: boolean;
    permissions: MediumPermissions;
    targetUrl: string;
    resolvedTargetUrl: string;
    trialDuration?: number | undefined;
    title?: string;
    scanUrl?: string;
    order?: {
        id: string;
        number: string;
        summary: AdminOrderSummary;
    };
    nfcWriteCount?: number;
    shipped?: boolean;
    shippedAt?: string;
    highlights?: {
        title?: string[];
        profileFullName?: string[];
        kompanyName?: string[];
        profileTitle?: string[];
    };
};

export type Product = {
    id: string;
    title: string;
};

export type MediumProduct = Product & {
    mediumType: MediumType;
    custom: boolean;
    namePrintable: boolean;
};

export type ExtraProduct = Product & {};

export type OrderLineItem = {
    id: string;
    sku: string;
    quantity: number;
    product: MediumProduct | ExtraProduct;
};

export type OrderLineItemMedium = OrderLineItem & {product: MediumProduct};

function isMediumProduct(
    product: MediumProduct | ExtraProduct,
): product is MediumProduct {
    return 'mediumType' in product;
}
export function isMediumOrderLineItem(
    item: OrderLineItem,
): item is OrderLineItemMedium {
    return isMediumProduct(item.product);
}

export type OrderProductSubstitution = {
    sourceCode: string;
    targetCode: string;
    quantity: number;
};

export type OrderProductAddition = {
    code: string;
    quantity: number;
    reason: OrderProductAdditionReason | undefined;
};

export enum OrderProductAdditionReason {
    FreeProductsAdded = 'free-products-added',
    SubstitutionImpliesAddition = 'substitution-implies-addition',
}

export enum InfoCategory {
    Profile = 0,
    Kompany = 1,
}

export type SocialSelection = {
    cat: InfoCategory;
    type: string;
};

export enum SocialStyleOption {
    Basic = 'basic',
    S1 = 's1',
}

export type SocialStyle = SocialStyleOption | undefined;

export enum PostalAddressSource {
    Profile = 'profile',
    Kompany = 'kompany',
}

export type SignatureStyle = {
    profileLink: boolean;
    textBefore?: JSONContent | null;
    textAfter?: JSONContent | null;
    linkLabel: string;
    primaryColor: string;
    secondaryColor: string;
    fontFamily: string;
    displayAvatar: boolean;
    displayLogo: boolean;
    roundedAvatar: boolean;
    coefficient: number;
    logoSize: number;
    displayTitle: boolean;
    displayDepartment: boolean;
    displayPhone: boolean;
    displayWebsite: boolean;
    displayPostalAddress: boolean;
    postalAddressSource: PostalAddressSource;
    displayEmail: boolean;
    socials: SocialSelection[] | undefined; // undefined means all
    socialStyle: SocialStyle;
};

export enum AssignmentMode {
    All = 0,
    Kompany = 1,
    Team = 2,
    Profile = 3,
    Model = 4,
}

export type ItemAssignment = {
    id: string;
    assignmentMode: AssignmentMode;
    profiles?: string[];
    teams?: string[];
    kompanies?: string[];
    models?: string[];
};

export type SignatureModel = {
    id: string;
    title: string;
    description: string;
    owner: User;
    style: SignatureStyle;
    priority: number;
    createdAt: string;
    updatedAt: string;
    customContent: Readonly<boolean>;
    content?: JSONContent | null;
};

export type Campaign = {
    id: string;
    title: string;
    description: string;
    targetUrl: string;
    banner: Image;
    enabled: boolean;
    active: boolean;
    owner: User;
    priority: number;
    createdAt: string;
    updatedAt: string;
    allKompanies: boolean;
    allProfiles: boolean;
    startsAt?: string;
    endsAt?: string;
};

export type LeadExtraFields = Record<string, string | null | undefined>;
export type ExchangeLead = {
    id: string;
    leadLabel: string;
    formId?: string;
    form?: LeadForm;
    firstName: string;
    lastName: string;
    jobTitle?: string;
    kompanyName?: string;
    website?: string;
    kompanyOrigin?: Kompany;
    note?: string;
    profile?: Profile | string;
    profileSafeKey?: string;
    address?: {
        address?: string;
        address2?: string;
        region?: string;
        city?: string;
        zipCode?: string;
        country: string;
    };
    profileLabel?: string;
    email?: string;
    phone?: string;
    phone2?: string;
    date: string;
    owner?: User;
    imageOrigin?: Image;
    tags: Tag[];
    transferSuggestion?: {
        id: string;
        displayName: string;
    };
    extraFields: LeadExtraFields;
    enrichment?: LeadEnrichment | null;
    enrichedFields?: string[];
};

export enum LeadEnrichmentStatus {
    Pending = 'pending',
    Done = 'done',
    Failed = 'failed',
    Skipped = 'skipped',
    NoResult = 'no_result',
}

export type LeadEnrichmentExtra = {
    civility?: string;
    firstName?: string;
    lastName?: string;
    fullName?: string;
    jobTitle?: string;
    company?: string;
    website?: string;
    linkedin?: string;
    companyLinkedin?: string;
    siren?: string;
    siret?: string;
    vat?: string;
    nbEmployees?: string;
    employeeCount?: string;
    companyTurnover?: string;
    industry?: string;
    naf5Code?: string;
    naf5Des?: string;
    jobLevel?: string;
    jobFunction?: string;
    companyAddress?: {
        address?: string;
        zipCode?: string;
        city?: string;
        country?: string;
    };
    emails?: {email: string; qualification: string | null}[];
    phone?: string;
    mobilePhone?: string;
};

export type LeadEnrichment = {
    status: LeadEnrichmentStatus;
    requestedAt?: string;
    completedAt?: string | null;
    fields?: Partial<
        Record<
            | 'firstName'
            | 'lastName'
            | 'email'
            | 'phone'
            | 'phone2'
            | 'jobTitle'
            | 'kompanyName'
            | 'website',
            string
        >
    >;
    extra?: LeadEnrichmentExtra;
    error?: string | null;
};

export type ExtractedLeadData = {
    firstName?: string;
    lastName?: string;
    kompanyName?: string;
    jobTitle?: string;
    email?: string;
    phone?: string;
    phone2?: string;
    address?: {
        address?: string;
        address2?: string;
        region?: string;
        city?: string;
        zipCode?: string;
        country?: string;
    };
    extra?: {
        otherEmails?: string[];
        otherPhones?: string[];
        websites?: string[];
        linkedIn?: string;
    };
    guessedCountry?: string;
    rawText?: string;
};

export enum LeadCaptureExtractError {
    NotReadable = 1,
    LeadIncomplete = 2,
}

export type LeadCapture = {
    id: string;
    profileLabel?: string;
    date: string;
    qrContent?: string;
    qrResolvedUrl?: string;
    matchedAdapter?: string;
    image?: Image;
    data?: ExtractedLeadData;
    extractError?: LeadCaptureExtractError;
    invalidLead?: ExchangeLead;
    leadViolations?: ViolationList;
};

export enum OrderStatusCode {
    Created = 'created',
    NonCustom = 'non_custom',
    AutoPilotStarted = 'autopilot_started',
    HumanHandled = 'human_handled',
    PrintConfigured = 'print_configured',
    PrintInfoRefused = 'print_info_refused',
    Bat = 'bat',
    BatRefused = 'bat_refused',
    Processing = 'processing',
    PartiallyShipped = 'partially_shipped',
    FullyShipped = 'fully_shipped',
    Delivered = 'delivered',
    Cancelled = 'cancelled',
    Refunded = 'refunded',
    OnHold = 'on_hold',
}

export enum OrderStatus {
    Created = 0,
    NonCustom = 10,
    HumanHandled = 11,
    AutoPilotStarted = 1,
    PrintConfigured = 2,
    PrintInfoRefused = 20,
    Bat = 3,
    BatRefused = 30,
    Processing = 4,
    PartiallyShipped = 60,
    FullyShipped = 6,
    Delivered = 7,
    Cancelled = 40,
    Refunded = 41,
    OnHold = 8,
}

export type OrderStatusTransition = {
    name: string;
    label: string;
    metadata?: Record<string, object | string | number | boolean | null>;
};

export type OrderProductMatchIssue = {
    code: string;
    title: string;
    count: number;
    expected: number;
};

export type OrderShippedProduct = {
    code: string;
    title: string;
    quantity: number;
};

export type AdminOrderSummary = {
    id: string;
    number: string;
    status: OrderStatus;
    statusCode: OrderStatusCode;
    wooCommerceStatus: string;
    createdByAnAdmin: boolean;
    payloadProducts: OrderLineItem[];
    hasProductAlterations: boolean;
    products: OrderLineItem[];
    otherProducts: OrderLineItem[];
    shippedProducts: OrderShippedProduct[];
    mediumsMatchedAt: string;
    email: string;
    shippingAddress: {
        firstName: string;
        lastName: string;
        companyName: string;
        address1: string;
        address2: string;
        zipCode: string;
        city: string;
        country: string;
        phone: string;
    };
    trackingNumber: string;
};

export type OrderProductMismatches = {
    totalExpected: number;
    totalCount: number;
    unexpected: OrderProductMatchIssue[];
    missing: OrderProductMatchIssue[];
    tooMany: OrderProductMatchIssue[];
};

export type Order = {
    id: string;
    owner?: User;
    ongoingShipment?: OrderShipment;
    summary?: AdminOrderSummary;
    wcDetailUrl?: string;
    availableTransitions?: OrderStatusTransition[];
    number: string;
    email: string;
    setupUrl?: string;
    adminTitle: string;
    date: string;
    assigned: boolean;
    lineItems: OrderLineItem[];
    statusCode: OrderStatusCode;
    configurable?: boolean;
    printConfig?: OrderPrintConfig;
    mediums?: Medium[];
    invalidMessage?: string | null;
    currentProductMismatches?: OrderProductMismatches;
};

export type OrderShipment = {
    id: string;
    orderSummary: AdminOrderSummary;
    productSubstitutions: OrderProductSubstitution[];
    productAdditions: OrderProductAddition[];
    shipped: boolean;
    shippedAt: string;
    shippedProducts: OrderShippedProduct[];
};

export type OrderStatusWorkflowLog = {
    id: string;
    date: string;
    fromStatusCode: OrderStatusCode;
    transition: string;
    userIdentifier?: string | null;
    error?: string | null;
    validationBypassedReason?: string | null;
};

export type OrderPrintConfig = {
    id: string;
    logos: OrderPrintConfigLogo[];
    products: OrderPrintConfigProduct[];
    newProfileCount: number;
    additionalInfo: string;
    saved: boolean;
    submitted: boolean;
    inviteEmailSentAt: string;
};

export type OrderPrintConfigProduct = {
    id: string;
    productCode: string;
    productTitle?: string;
    profileId?: string | null;
    kompanyId?: string | null;
    kompanyName?: string;
    firstName: string;
    lastName: string;
    printContactInfo: boolean;
};

export type OrderPrintConfigLogo = {
    id: string;
    url: string;
    originalFilename: string;
    filename: string;
};

export type BatchImportData = RowData[];

type EntityChanges = {
    medium: number;
    profile: number;
    user: number;
};

export type BatchImportSummary = {
    insertions: EntityChanges;
    updates: EntityChanges;
    deletions: EntityChanges;
};

type DefaultsFormMedium = {
    medium_activated?: boolean | undefined;
    medium_order?: string | undefined;
    product_sku?: string | null;
    print_context?: number | undefined;
};

type DefaultsFormProfile = {
    exchanges_enabled?: boolean | undefined;
};

export enum IntegrationCapability {
    Lead = 'lead',
    UserDirectory = 'user_directory',
}

export type IntegrationConfig = Record<string, any>;

export enum IntegrationLeadFilterMode {
    All = 0,
    Profiles = 1,
    Kompanies = 2,
}

// Which leads reach the integration. The mode decides which list applies; both are kept, so
// switching back and forth loses no selection. Read from its own endpoint: the lists can be
// long and no other view of the integration needs them.
export type IntegrationLeadFilter = {
    id: string;
    mode: IntegrationLeadFilterMode;
    profiles: Profile[];
    kompanies: Kompany[];
};

export type Integration = {
    id: string;
    name: string | null;
    integration: string;
    config: IntegrationConfig;
    errors: string[];
    capabilities: IntegrationCapability[];
    enabled: boolean;
    // When our copy of the service's member directory was last brought in line
    membersSyncedAt: string | null;
    createdAt: string;
};

export enum IntegrationInstallMode {
    Direct = 'direct',
    OAuth = 'oauth',
}

export type IntegrationService = {
    id: string;
    name: string;
    title: string;
    description: string;
    icon: string;
    configuration: Integration | null | undefined;
    installMode: IntegrationInstallMode;
    createdAt: string;
};

export type IntegrationAuthorization = {
    url: string;
};

export type IntegrationConfigChoice = {
    label: string;
    value: string;
};

export type IntegrationConfigOptions = {
    id: string;
    metadata: Record<string, any>;
};

// Values a setting can take: they cost the service a call, so they are fetched on their own
// endpoint when a field is opened. One page, plus whatever the service can be asked to look up
export type IntegrationConfigChoices = {
    id: string;
    field: string;
    items: IntegrationConfigChoice[];
};

export type IntegrationProfileOwners = {
    id: string;
    owners: Record<string, string>;
};

type RemoteProfileData = {
    firstName?: string;
    lastName?: string;
    jobTitle?: string;
};

export type IntegrationProfileLink = {
    id: string;
    data: RemoteProfileData;
    externalId: string;
    enabled: boolean;
    profile?: Profile;
    updatedAt: string;
    metadata?: Record<string, any>;
    createdAt: string;
};

export type BatchImport = {
    id: string;
    name: string;
    version: number;
    currentVersion?: number;
    note?: string;
    data?: BatchImportData;
    contextUser: User | string | null;
    owner: User | string | null;
    columns?: string[];
    defaults: DefaultsFormProfile & DefaultsFormMedium;
    defaultsForMedium?: DefaultsFormMedium;
    defaultsForProfile?: DefaultsFormProfile;
    ownerEmail: string;
    organizationName: string;
    organizationId?: string;
    createdAt: string;
    importedAt: string;
    profileCreditsLeft?: number;
    summary: BatchImportSummary;
    generatePrintBoardUrl?: string;
    permissions: {
        edit: boolean;
        delete: boolean;
    };
};

export type PlanSubscription = {
    id: string;
    title: string;
    planName: string | undefined;
    options: PlanOptions;
    organization: Organization;
    customOptions?: Partial<PlanOptions> | null | undefined;
    trial: boolean;
    trialEndsAt?: string;
    cancelledAt?: string;
    chargebee: boolean;
    chargebeeUpgradable: boolean;
    startsAt: string;
    endsAt: string;
    note: string | undefined;
    active: boolean;
    profileQuantity: number;
    profileCount?: number;
    minProfile: number | undefined;
    engagementExpiresAt: string;
    permissions: {
        edit: boolean;
        delete: boolean;
    };
    highlights?: {
        organizationName?: string[];
        ownerEmail?: string[];
        note?: string[];
    };
};

export type Stats = {
    id: string;
    ts: number;
    v: number;
    ms: number;
    ps: number;
    l: number;
};

export type StatKeys = {
    views: number;
    mediumScans: number;
    profileScans: number;
    leads: number;
};

export type StatTopItem = {
    key: string;
    title?: string | ReactNode;
    subtitle?: string | ReactNode;
    count: number;
};

export type TopLink = {
    key: string;
    type: LinkType;
    entityType: EntityType;
    entityId: string;
    entityLabel?: string;
    title?: string;
    url?: string;
    count: number;
};

export enum EntityType {
    Profile = 1,
    Kompany = 2,
}

export enum LinkType {
    Social = 1,
    Link = 2,
    Block = 3,
    Website = 4,
}

export type StatTop = {
    mediumScans: StatTopItem[];
    profileScans: StatTopItem[];
    profileViews: StatTopItem[];
    leads: StatTopItem[];
};

export type IdentityProvider = {
    id: string;
    name: string;
    provider: string;
    providerTitle: string;
    config?: Record<string, any>;
    urls?: {
        redirectUri: string;
        logoutUrl: string;
    };
};

export type IdentityProviderField = {
    name: string;
    label: string;
    helper?: string;
    required?: boolean;
};

export type IdentityProviderService = {
    id: string;
    title: string;
    fields: IdentityProviderField[];
};

export type Organization = {
    id: string;
    identifier?: string | null;
    name: string | null;
    resolvedName: string;
    current: boolean;
    owner: User | string;
    named: boolean;
    multi: boolean;
    defaultCountry: string | undefined;
    gtmEnabled?: boolean | undefined;
    plan?: OrganizationPlan;
    dashboardUrl: string;
    mfaRequired: boolean;
    passwordMinLength?: number | undefined | null;
    ssoOnly?: boolean;
    ssoConfigured?: boolean;
    ssoRequired?: boolean;
    adminsCanUsePassword?: boolean;
    adminContact?: string;
    exchangesEnabled?: boolean;
    leadWelcomeEmailEnabled?: boolean;
    canDisableLeadWelcomeEmail?: boolean;
    leadEnrichmentEnabled?: boolean;
    leadAutoEnrichmentEnabled?: boolean;
    fieldPermissionsEnabled?: boolean;
    adminsCanSeeAllLeads?: boolean;
    membersCanSeeGlobalStats?: boolean;
    membersCanSeeEachOther?: boolean;
    identityProviders: IdentityProvider[];
    locale?: string | null;
    permissions: {
        edit: boolean;
        manage: boolean;
        transfer: boolean;
        viewGlobalStats: boolean;
        listMembers: boolean;
        acl: boolean;
        createKompany: boolean;
        createTag: boolean;
        createProfile: boolean;
        createProfileRequiresKompany: boolean;
        manageSubscriptions: boolean;
        canTransferLeads: boolean;
    };
    highlights?: {
        name?: string[];
        ownerEmail?: string[];
    };
    profileCount?: number;
    userCount?: number;
    planEndsAt?: string;
    createdAt: string;
};

export enum PlanOption {
    Profiles = 'profiles',
    Stats = 'stats',
    Sso = 'sso',
    Report = 'report',
    Sheet = 'sheet',
    Signature = 'signature',
    SignatureCustom = 'signatureCustom',
    LeadForm = 'leadForm',
    AppClip = 'appClip',
    OpenGraph = 'openGraph',
    OrgAcl = 'orgAcl',
    FieldPermissions = 'fieldPermissions',
    ExtendedLeads = 'extendedExchanges',
    CustomLeadMail = 'customLeadMail',
    Tags = 'tags',
    PaperScan = 'paperScan',
    Integrations = 'integrations',
    MoreTranslations = 'moreTranslations',
    Assets = 'assets',
    NoBranding = 'noBranding',
    CustomSubDomain = 'customSubDomain',
    LeadEnrichment = 'leadEnrichment',
}

export enum FieldPermissionUserType {
    User = 0,
    Team = 1,
}

export type FieldPermission = (
    | {
          userType: FieldPermissionUserType.Team;
          team?: Team | null;
      }
    | {
          userType: FieldPermissionUserType.User;
          user?: User | null;
      }
) & {
    id: string;
    objectType: AclObjectType;
    objectId: string | null;
    userType: FieldPermissionUserType;
    userId: string | null;
    resolving?: boolean;
    rules: FieldPermissionsRules;
};

export type PlanOptions = Record<PlanOption, boolean | string | number>;

export type OrganizationPlan = Pick<
    PlanSubscription,
    | 'id'
    | 'title'
    | 'options'
    | 'active'
    | 'trial'
    | 'chargebee'
    | 'chargebeeUpgradable'
>;

export type Team = {
    id: string;
    name: string;
    memberCount: number;
    highlights?: Highlights;
};

export type TeamMember = {
    id: string;
    user: User;
    roles: string[];
};

export enum MemberInviteStatus {
    Sent = 0,
    Accepted = 1,
    Queued = 2,
    Declined = 3,
    NotInvited = 4,
}

export type MemberInvite = {
    id: string;
    email: string;
    hasAccount: boolean;
    status: MemberInviteStatus;
    organization: Omit<Organization, 'owner'>;
    createdAt: string;
    message?: string | null;
    lastEmailSentAt?: string | null;
    declinedAt?: string | null;
    previouslyDeclinedAt?: string | null;
    expired?: boolean;
};

export type AccountTransfer = {
    id: string;
    fromUserEmail: string;
    email: string;
    token: string;
};

export type ApiFileResolved = {
    id: string;
    url: string;
    svgSpec: SVGSpec;
};

export type MediumDisableReason = {
    id: string;
    label: string;
};

export type EstimationItem = {
    description: string;
    amount: number;
    quantity: number;
};

export type Estimation = {
    isCredit: boolean;
    items: EstimationItem[];
    creditItems: EstimationItem[];
    subTotal: number;
    total: number;
    subTotalDue: number;
    amountDue: number;
    currencyCode: string;
};

export type AssetBase = {
    id: string;
    name: string;
    locale: string;
    url: string;
    size: number;
    type: string | undefined;
    thumbUrl?: string;
    createdAt: string;
};

export type AssetTranslation = {} & AssetBase;

export type Asset = {
    usage: number;
    translations: AssetTranslation[];
} & AssetBase;

export type LeadFormOptions = {};

export type LeadForm = {
    id: string;
    title: string;
    description: string;
    owner: User;
    form?: FormSpec;
    enabled: boolean;
    scanDisabled?: boolean;
    priority: number;
    createdAt: string;
    updatedAt: string;
};

export type WithTags = {tags: Tag[]};

export enum SsoIntent {
    Login = 'l',
    Register = 'r',
    Link = 'lk',
}

export type SocialConnectLink = {
    id: string;
    provider: string;
};

export enum GuidedTourId {
    MainMenu = 'main_menu',
    ProfileDrawer = 'profile_drawer',
    ProfileEdit = 'profile_edit',
}

export type GuidedTour = {
    id: GuidedTourId;
    completed: boolean;
};

export type ArchiveExportHandlers = {
    started: (data: {itemCount: number}) => void;
    progress: (data: {itemsDone: number}) => void;
    allDone: (data: {url: string}) => void;
    error: (data: {errorMessage: string}) => void;
};

export type ArchiveExportStart = {
    channel: string;
    events: {[K in keyof ArchiveExportHandlers]: string};
};
